import { NativeSelect, NativeSelectOption } from '@worldcoo/ui/components/native-select';
import { Flag } from '@/lib/country-flags';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { Suspense } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { isNotFound } from '@tanstack/react-router';

const languageOptions = [
	{ value: 'ca' as const, label: 'Català' },
	{ value: 'de' as const, label: 'Deutsch' },
	{ value: 'en' as const, label: 'English' },
	{ value: 'es' as const, label: 'Español' },
	{ value: 'eu' as const, label: 'Euskera' },
	{ value: 'fr' as const, label: 'Français' },
	{ value: 'gl' as const, label: 'Galego' },
	{ value: 'it' as const, label: 'Italiano' },
	{ value: 'nl' as const, label: 'Nederlands' },
	{ value: 'pt' as const, label: 'Português' },
];

export function LanguageSelect(props: { className?: string }) {
	return (
		<Suspense fallback={<LanguageSelectPrimitive languageOptions={languageOptions} />}>
			<SuspensedLanguageSelect className={props.className} />
		</Suspense>
	);
}

function SuspensedLanguageSelect(props: { className?: string }) {
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<LanguageSelectPrimitive
			languageOptions={languageOptions.filter((option) => metadata.locales.includes(option.value))}
			className={props.className}
		/>
	);
}

function LanguageSelectPrimitive(props: { languageOptions: typeof languageOptions; className?: string }) {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	if (props.languageOptions.length <= 1) return;

	return (
		<div className={cn('flex items-center gap-1.5', props.className)}>
			<Flag locale={language} className="pointer-events-none size-5" />
			<NativeSelect
				className="[&_select]:border-none [&_select]:bg-transparent [&_select]:text-base [&_select]:shadow-none"
				name="language-select"
				value={language}
				onChange={(e) => {
					const selected = props.languageOptions.find((o) => o.value === e.target.value);
					if (selected) changeLanguage(selected.value);
				}}
				aria-label={t`Select language`}
			>
				{props.languageOptions.map(({ value, label }) => (
					<NativeSelectOption key={value} value={value}>
						{label}
					</NativeSelectOption>
				))}
			</NativeSelect>
		</div>
	);
}
